import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { GitBranch, Users, List, FileText, ArrowLeft, Edit2, Loader2, Building2, Package, Settings } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CompanyConfigOverridesManager } from './CompanyConfigOverridesManager';
import { PhaseManagement } from './PhaseManagement';
import { DefaultConfigItemsManager } from './DefaultConfigItemsManager';
import { TemplateDefaultConfigManager } from './TemplateDefaultConfigManager';
import { CompanyConfigurationGuide } from './CompanyConfigurationGuide';
import { CompanyEditDialog } from './CompanyEditDialog';
import { projectId } from '../utils/supabase/info';
import type { Project } from '../types';
import { isSystemAdmin, hasRole } from '../utils/roleUtils';

interface ConfigurationPageProps {
  accessToken: string;
  currentUser: any;
  /** A full page with a Back button; without it the page sits inside a tab (System Admin › Configuration, Sell › Catalog). */
  onBack?: () => void;
  initialCompanyId?: string | null;
  onNavigateToManage?: (companyId: string, section: 'projects' | 'teams' | 'workflows' | 'workTypes' | 'requests' | 'fields') => void;
}

interface Company {
  id: string;
  name: string;
}

export function ConfigurationPage({ 
  accessToken, 
  currentUser,
  onBack,
  initialCompanyId,
  onNavigateToManage
}: ConfigurationPageProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(initialCompanyId || '');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // The imported isSystemAdmin is a function; it was used as a boolean, which made everyone a system admin
  // here and sent consultants to admin-only routes (RESTORE_PLAN R3.6).
  const userIsSystemAdmin = isSystemAdmin(currentUser);
  const isConsultant = hasRole(currentUser, 'consultant') || hasRole(currentUser, 'cfAdmin') || hasRole(currentUser, 'bdRep');

  // Load companies list for System Admins and Consultants
  useEffect(() => {
    const loadCompanies = async () => {
      if (!userIsSystemAdmin && !isConsultant) {
        // Regular users only see their own company
        setSelectedCompanyId(currentUser.companyId);
        setCompaniesLoading(false);
        return;
      }

      setCompaniesLoading(true);
      try {
        const endpoint = userIsSystemAdmin 
          ? '/admin/companies'
          : '/consultant/companies';
        
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd${endpoint}`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          // Admin endpoint returns { companies: [...] }, consultant returns array directly
          const companiesList = userIsSystemAdmin ? data.companies : data;
          setCompanies(companiesList);
          
          // Set initial company if provided
          if (initialCompanyId && companiesList.some((c: Company) => c.id === initialCompanyId)) {
            setSelectedCompanyId(initialCompanyId);
          } else {
            // Don't auto-select - let the user choose
            setSelectedCompanyId('');
          }
        } else {
          setError('Failed to load companies');
        }
      } catch (err) {
        console.error('Error loading companies:', err);
        setError('Error loading companies');
      } finally {
        setCompaniesLoading(false);
      }
    };

    loadCompanies();
  }, [accessToken, userIsSystemAdmin, isConsultant, currentUser.companyId, initialCompanyId]);

  // Load company data when selected company changes
  useEffect(() => {
    const loadCompanyData = async () => {
      if (!selectedCompanyId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        // Load company details
        const companyResponse = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/companies/${selectedCompanyId}`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        );

        if (companyResponse.ok) {
          const companyData = await companyResponse.json();
          setSelectedCompany(companyData.company);
          setTotalUsers(companyData.users?.length || 0);
        }

        // Load projects/configuration data
        const dataResponse = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${selectedCompanyId}`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        );

        if (dataResponse.ok) {
          const data = await dataResponse.json();
          setProjects(data.projects || []);
        } else {
          setProjects([]);
        }
      } catch (err) {
        console.error('Error loading company data:', err);
        setError('Error loading company configuration data');
      } finally {
        setLoading(false);
      }
    };

    loadCompanyData();
  }, [selectedCompanyId, accessToken]);

  // Default to 'guide' (Progress Tracker) when a company is selected, 'defaults' for System Admins when no company
  const [activeTab, setActiveTab] = useState<string>(
    userIsSystemAdmin && !initialCompanyId ? 'defaults' : 'guide'
  );

  // Update active tab when company selection changes - prioritize Progress Tracker
  useEffect(() => {
    if (selectedCompanyId) {
      setActiveTab('guide'); // Progress Tracker is the default for company-specific view
    } else if (userIsSystemAdmin) {
      setActiveTab('defaults'); // System Defaults for System Admins when no company selected
    }
  }, [selectedCompanyId, userIsSystemAdmin]);

  // Refresh configuration data when switching to the Progress Tracker tab
  useEffect(() => {
    if (activeTab === 'guide' && selectedCompanyId) {
      setRefreshKey(prev => prev + 1);
    }
  }, [activeTab, selectedCompanyId]);

  // Determine which tabs should be visible
  const showSystemDefaultsTab = userIsSystemAdmin;
  const showCompanyConfigTab = (userIsSystemAdmin || isConsultant) && selectedCompanyId;

  return (
    <div className={onBack ? 'min-h-screen bg-muted/30' : 'rounded-lg border bg-muted/30'} data-testid="configuration-page">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            {onBack ? (
              <Button variant="outline" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            ) : <span />}
            
            {/* Allow System Admins to clear company selection */}
            {userIsSystemAdmin && selectedCompanyId && (
              <Button 
                variant="outline" 
                onClick={() => {
                  setSelectedCompanyId('');
                  setActiveTab('defaults');
                }}
              >
                <Settings className="h-4 w-4 mr-2" />
                System Defaults Only
              </Button>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1>Configuration Management</h1>
              <p className="text-muted-foreground">
                {selectedCompanyId 
                  ? 'Manage implementation phases, configuration items, and company-specific settings'
                  : userIsSystemAdmin
                    ? 'Manage system-wide default configuration items for all companies'
                    : 'Select a company to manage configuration settings'
                }
              </p>
            </div>

            {/* Company Selector for System Admins and Consultants - always visible */}
            {(userIsSystemAdmin || isConsultant) && companies.length > 0 && (
              <div className="w-80">
                <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a company to configure" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          {company.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>
              <div>
                <p className="font-medium mb-1">{error}</p>
                <p className="text-sm opacity-90">
                  Please check the browser console for more details or try refreshing the page.
                </p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {companiesLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-3 text-muted-foreground">Loading companies...</span>
          </div>
        )}

        {!companiesLoading && loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-3 text-muted-foreground">Loading configuration data...</span>
          </div>
        )}

        {!companiesLoading && !loading && !selectedCompanyId && (userIsSystemAdmin || isConsultant) && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              {showSystemDefaultsTab && (
                <TabsTrigger value="defaults">
                  <Settings className="h-4 w-4 mr-2" />
                  System Defaults
                </TabsTrigger>
              )}
              {showSystemDefaultsTab && (
                <TabsTrigger value="templates">
                  <Package className="h-4 w-4 mr-2" />
                  Template Config
                </TabsTrigger>
              )}
              {isConsultant && (
                <TabsTrigger value="guide" disabled>
                  <FileText className="h-4 w-4 mr-2" />
                  Company Guide
                </TabsTrigger>
              )}
            </TabsList>

            {showSystemDefaultsTab && (
              <TabsContent value="defaults">
                {!selectedCompanyId && userIsSystemAdmin && (
                  <Card className="mb-6 bg-blue-50 border-blue-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-blue-600" />
                        Configure Company-Specific Settings
                      </CardTitle>
                      <CardDescription>
                        Select a company from the dropdown above to access implementation phases and company-specific settings.
                      </CardDescription>
                    </CardHeader>
                  </Card>
                )}
                <Alert className="mb-6">
                  <Settings className="h-4 w-4" />
                  <AlertDescription>
                    <strong>System Defaults Mode:</strong> Changes here apply to all companies. Use the company selector above for company-specific configuration.
                  </AlertDescription>
                </Alert>
                <DefaultConfigItemsManager accessToken={accessToken} currentUser={currentUser} />
              </TabsContent>
            )}

            {showSystemDefaultsTab && (
              <TabsContent value="templates">
                <Alert className="mb-6">
                  <Package className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Template-Specific Configuration:</strong> Seed default configuration items for specific consulting templates (Atlassian, ServiceNow, Ivanti, BMC Helix, Freshservice, etc.).
                    These are pre-built sets of configuration items based on industry best practices.
                  </AlertDescription>
                </Alert>
                <TemplateDefaultConfigManager accessToken={accessToken} currentUser={currentUser} />
              </TabsContent>
            )}

            {isConsultant && (
              <TabsContent value="guide">
                <Card>
                  <CardHeader>
                    <CardTitle>Select a Company</CardTitle>
                    <CardDescription>
                      {companies.length > 0 
                        ? 'Please select a company from the dropdown above to view its configuration'
                        : 'No companies are available in the system yet'}
                    </CardDescription>
                  </CardHeader>
                  {companies.length > 0 && (
                    <CardContent>
                      <p className="text-muted-foreground">
                        Use the company selector in the header to choose which company's configuration you want to review.
                      </p>
                    </CardContent>
                  )}
                </Card>
              </TabsContent>
            )}
          </Tabs>
        )}

        {!loading && selectedCompanyId && selectedCompany && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              {showSystemDefaultsTab && (
                <TabsTrigger value="defaults">
                  <Settings className="h-4 w-4 mr-2" />
                  System Defaults
                </TabsTrigger>
              )}
              {showSystemDefaultsTab && (
                <TabsTrigger value="templates">
                  <Package className="h-4 w-4 mr-2" />
                  Template Config
                </TabsTrigger>
              )}
              {showCompanyConfigTab && (
                <TabsTrigger value="company-config">
                  <Settings className="h-4 w-4 mr-2" />
                  Phase Configuration
                </TabsTrigger>
              )}
              {showCompanyConfigTab && (
                <TabsTrigger value="phases">
                  <GitBranch className="h-4 w-4 mr-2" />
                  Phases
                </TabsTrigger>
              )}
              <TabsTrigger value="guide">
                <FileText className="h-4 w-4 mr-2" />
                Progress Tracker
              </TabsTrigger>
            </TabsList>

            {showSystemDefaultsTab && (
              <TabsContent value="defaults">
                {selectedCompanyId && (
                  <Alert className="mb-6">
                    <Settings className="h-4 w-4" />
                    <AlertDescription>
                      <strong>System Defaults Mode:</strong> Changes here apply to all companies, not just {selectedCompany?.name}. Use the "Phase Configuration" tab for company-specific settings.
                    </AlertDescription>
                  </Alert>
                )}
                <DefaultConfigItemsManager accessToken={accessToken} currentUser={currentUser} />
              </TabsContent>
            )}

            {showSystemDefaultsTab && (
              <TabsContent value="templates">
                {selectedCompanyId && (
                  <Alert className="mb-6">
                    <Package className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Template-Specific Configuration:</strong> Seed default configuration items for specific consulting templates.
                      These changes affect the system-wide template library, not just {selectedCompany?.name}.
                    </AlertDescription>
                  </Alert>
                )}
                <TemplateDefaultConfigManager accessToken={accessToken} currentUser={currentUser} />
              </TabsContent>
            )}

            {showCompanyConfigTab && (
              <TabsContent value="company-config">
                <CompanyConfigOverridesManager 
                  accessToken={accessToken}
                  companyId={selectedCompanyId}
                  companyName={selectedCompany.name}
                />
              </TabsContent>
            )}

            {/* The company's implementation phases (the old ConsultantPanel's PhaseManagement; RESTORE_PLAN R3.4/R3.6) */}
            {showCompanyConfigTab && (
              <TabsContent value="phases">
                <PhaseManagement accessToken={accessToken} companyId={selectedCompanyId} companyName={selectedCompany.name} currentUser={currentUser} />
              </TabsContent>
            )}

            <TabsContent value="guide">
              <CompanyConfigurationGuide
                companyId={selectedCompanyId}
                companyName={selectedCompany.name}
                projects={projects}
                totalUsers={totalUsers}
                accessToken={accessToken}
                currentUser={currentUser}
                isConsultant={currentUser.role === 'consultant'}
                isSystemAdmin={currentUser.role === 'systemAdmin' || currentUser.isAdmin}
                refreshKey={refreshKey}
                onNavigateToProjects={() => onNavigateToManage?.(selectedCompanyId, 'projects')}
                onNavigateToTeams={() => onNavigateToManage?.(selectedCompanyId, 'teams')}
                onNavigateToWorkflows={() => onNavigateToManage?.(selectedCompanyId, 'workflows')}
                onNavigateToWorkTypes={() => onNavigateToManage?.(selectedCompanyId, 'workTypes')}
                onNavigateToRequests={() => onNavigateToManage?.(selectedCompanyId, 'requests')}
                onNavigateToFields={() => onNavigateToManage?.(selectedCompanyId, 'fields')}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}